/* ===== Shared Domain Types: Interfaces, Models, Structs ===== */
/* Split from types.ts — imports from utils.ts and systems/dnd5e/types.js */

import type {
  TextFontSize,
  FieldType,
  AbilityRecharge,
  SheetType,
  TokenActionCategory,
  AoeShape,
  EffectMode,
  TokenFilter,
  TerrainType,
  CoverType,
  MapObjectCategory,
  ObjectCover,
  ObjectState,
  MeasurementMode,
  GridStyle,
  HpBarVisibility,
  TokenSize,
  ViewOtherSheets,
  Role,
  NoteIcon,
  WallBlockingType,
  DoorState,
  JournalEntryType,
  JournalVisibility,
  QuestStatus,
  NpcDisposition,
  AlertLayout,
  HomebrewEntryType,
  TimeOfDay,
  TimelineEventType,
  TimelineEventStatus,
  TimelineEventSource,
  TimelineLinkTarget,
  CampaignSessionStatus,
  GameSystemId,
  NpcBehaviorProfile,
  PushEventType,
  CampaignTone,
  PersonaEmotion,
  VoiceConnectionStatus,
  ImportSource,
  ConflictStrategy,
  CampaignEntityType,
  PlayerPermissions,
  AiAbortStage,
} from './utils.js';

import type {
  Dnd5eSheetData,
  Dnd5eSessionSettings,
  SpellSlotsData,
  HitDiceData,
  SpellbookEntry,
  DeathSaveState,
  ClassEntry,
} from './systems/dnd5e/types.js';

// Re-export D&D 5e types for barrel compatibility
export type {
  SpellSlotLevel,
  PactMagicSlot,
  SpellSlotsData,
  SpellbookEntry,
  HitDiceData,
  DeathSaveStatus,
  DeathSaveState,
  ClassEntry,
  Dnd5eSheetData,
  Dnd5eSessionSettings,
} from './systems/dnd5e/types.js';

// --- Basic Geometry ---

export interface Point {
  x: number;
  y: number;
}

// --- Drawing ---

export interface Stroke {
  id: string;
  type: 'path' | 'rect' | 'circle' | 'line';
  points?: Point[];
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  color: string;
  lineWidth: number;
  // AI-assigned metadata (Sub-spec C)
  label?: string;
  temporary?: boolean;
  duration?: number;
  createdAt?: string;
}

// --- Text Objects ---

export interface TextObject {
  id: string;
  x: number;
  y: number;
  text: string;
  fontSize: TextFontSize;
  color: string;
  rotation?: number;
  createdBy: string;
}

/** Clipboard data for token copy/paste (frontend-only) */
export interface TokenClipboard {
  token: SerializedToken;
  sourcePageId: string;
}

// --- Character Sheets ---

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  defaultValue?: string | number | boolean | string[];
  min?: number;
  max?: number;
  options?: string[];
  formula?: string;
  rollExpression?: string;
}

export interface TemplateSection {
  key: string;
  label: string;
  fields: FieldDefinition[];
  repeating?: boolean;
  type?: 'default' | 'spellSlots';
}

export interface TokenBinding {
  hp?: string;
  maxHp?: string;
  ac?: string;
  conditions?: string;
}

export interface SheetTemplate {
  id: string;
  name: string;
  builtIn: boolean;
  sections: TemplateSection[];
  tokenBinding: TokenBinding;
  supportsStatBlock?: boolean;
}

export interface Ability {
  id: string;
  name: string;
  description: string;
  maxUses: number | null;
  currentUses: number;
  restType: AbilityRecharge;
  /** Optional origin label (e.g. class feature, racial trait, item) for HUD display */
  source?: string;
}

// --- Repeating Sections ---

export interface RepeatingSectionRow {
  rowId: string;
  values: Record<string, string | number | boolean | string[]>;
}

// --- Token Actions ---

export interface TokenAction {
  id: string;
  name: string;
  rollExpression: string;
  sortOrder: number;
  icon?: string;
  color?: string;
  category?: TokenActionCategory;
}

// --- AoE Templates ---

export interface AoeTemplate {
  id: string;
  shape: AoeShape;
  originX: number;
  originY: number;
  sizeFt: number;
  rotation: number;
  color: string;
  placedBy: string;
  label?: string;
}

export interface AoeAffectedResult {
  templateId: string;
  tokenIds: string[];
}

// --- Roll System ---

export interface RollRequest {
  sheetId: string;
  rollName: string;
  expression: string;
  fieldValues: Record<string, number>;
  isWhisper: boolean;
  isDualD20: boolean;
  attackDamage?: {
    expression: string;
    type: string;
  };
  /** GN: what kind of d20 roll this is (the sheet says so; older clients leave it out and the server guesses by name). */
  rollKind?: 'attack' | 'check' | 'skill' | 'save' | 'initiative';
  /** The ability of a save / check («dex»). */
  stat?: string;
  /** Advantage / disadvantage the player asked for on the sheet (conditions are added by the server). */
  advantage?: 'advantage' | 'disadvantage';
  /** Set by the server: the resolved mode — with it and two results, `kept` says which one counts. */
  mode?: 'normal' | 'advantage' | 'disadvantage';
}

export interface DieResult {
  sides: number;
  value: number;
  /** Rolled but not counted: dropped by a «keep highest / lowest» (4d6kh3). */
  dropped?: boolean;
}

export interface SingleRollResult {
  dice: DieResult[];
  modifier: number;
  total: number;
  isCritical: boolean;
  isFumble: boolean;
}

export interface RollResult {
  id: string;
  sheetId: string;
  characterName: string;
  rollName: string;
  expression: string;
  results: SingleRollResult[];
  damage?: {
    dice: DieResult[];
    modifier: number;
    total: number;
    type: string;
    isCriticalDamage: boolean;
  };
  isWhisper: boolean;
  timestamp: string;
  /** GN: advantage / disadvantage — two results, `kept` is the one that counts. */
  mode?: 'advantage' | 'disadvantage';
  kept?: number;
  /** Why (condition names, «manual», «effect») — for the roll card. */
  modeFrom?: string[];
}

// --- Active Effects ---

export interface EffectSource {
  type: 'item' | 'spell' | 'condition' | 'feature' | 'custom';
  id: string;
  name: string;
}

export interface ActiveEffect {
  id: string;
  source: EffectSource;
  /**
   * Optional display label for the effect (e.g. a custom status name typed by
   * the user). When absent, the HUD derives a label from target/mode/value —
   * which for a pure-label status renders as junk like "self add 0". Persisted
   * so a custom status shows the name the user actually entered.
   */
  name?: string;
  target: string;
  mode: EffectMode;
  value: number;
  priority: number;
  disabled: boolean;
  /**
   * Server-maintained, never taken from a client: the sheet's effect ledger. For each plain field the sheet's effects
   * or conditions change (speed, abilities, max HP — not formula fields or the derived AC), its value without them
   * (`base`) and with them (`applied`) at the last recalculation. The same on every effect of the sheet, so it lives
   * exactly as long as the sheet has an effect. Absent on effects saved before it existed: the field's current value
   * is then taken as the base once.
   */
  bases?: Record<string, EffectBaseline>;
  /**
   * D&D 5e: the effect lasts while this caster concentrates on this spell, and ends with that concentration (on
   * whichever sheet it sits). Set explicitly when the effect is added and checked against the caster's concentration
   * then; never inferred from the spell slug.
   */
  concentration?: EffectConcentrationLink;
  /**
   * Automatic end, written only by the server (never taken from a client): absent = the effect is removed by hand.
   * See EffectTurnStartExpiry.
   */
  expiry?: EffectTurnStartExpiry;
}

/**
 * Why an automatic end stopped waiting. Stored; only `fight-ended` is lifted by itself — when «Отменить» an end brings
 * back the same fight clock. Every other pause stays until the GM sets the expiry again or clears it.
 */
export type EffectExpiryPause = 'anchor-removed' | 'anchor-ambiguous' | 'anchor-skipped' | 'clock-full' | 'clock-rewound' | 'fight-ended';

/**
 * «До начала следующего хода» of the creature of the SAME sheet the effect sits on (Shield: the caster is the target).
 * The anchor is the sheet's only token in the fight — no token id is stored, so the effect never names a hidden token.
 * It ends at the counted start of that turn in `expectRound` of fight `fightId`, admitted when the clock had counted `atSeq`
 * (docs/backlog/tia-turn-clock-design-2026-10-28.md).
 */
export interface EffectTurnStartExpiry {
  kind: 'turn-start';
  fightId: string;
  expectRound: number;
  atSeq: number;
  paused?: EffectExpiryPause;
}

/** What a sheet:effect-add / effect-update request may ask for: only the exact «until the start of its next turn». */
export interface EffectExpiryRequest {
  kind: string;
}

/** The command receipt of an effect's end: removed by hand, or automatically at the start of its creature's turn. */
export type EffectExpiryReceipt =
  | { mode: 'manual' }
  | { mode: 'auto'; kind: 'turn-start'; expectRound: number; text: string };

export interface EffectBaseline {
  base: number;
  applied: number;
}

export interface EffectConcentrationLink {
  casterSheetId: string;
  spellSlug: string;
}

// --- Audit Log (T204) ---

/** Single audit log entry tracking a mutation to a CharacterSheet field. */
export interface AuditEntry {
  /** Unique entry id */
  id: string;
  /** ISO 8601 timestamp of the mutation */
  timestamp: string;
  /** Origin of the mutation */
  mutatedBy: 'ai' | 'player' | 'system';
  /** Dotted field path on the sheet (e.g. "values.hp", "abilities[0].currentUses") */
  field: string;
  /** Previous value (JSON-serialisable) */
  before: unknown;
  /** New value (JSON-serialisable) */
  after: unknown;
}

// --- AI Observations (diagnostic, GM+owner only) ---

export const AI_OBSERVATION_SOURCES = ['roleplay-injection', 'ai-tool-error'] as const;
export type AiObservationSource = (typeof AI_OBSERVATION_SOURCES)[number];

export const AI_OBSERVATION_SEVERITIES = ['info', 'warn', 'error'] as const;
export type AiObservationSeverity = (typeof AI_OBSERVATION_SEVERITIES)[number];

export const AI_OBSERVATIONS_MAX = 50;

export interface AiObservation {
  id: string;
  createdAt: string;
  source: AiObservationSource;
  severity: AiObservationSeverity;
  message: string;
  details?: Record<string, unknown>;
}

// --- Character Sheet ---

export interface CharacterSheet {
  id: string;
  templateId: string;
  sheetType: SheetType;
  name: string;
  ownerName: string | null;
  ownerId?: string | null;
  values: Record<string, string | number | boolean | string[]>;
  abilities: Ability[];
  publicFields: string[];
  repeatingData: Record<string, RepeatingSectionRow[]>;
  tokenActions: TokenAction[];
  portrait: string | null;
  tokenDescription: string;
  activeEffects: ActiveEffect[];
  aiContext?: CharacterAiContext;
  /** D&D 5e-specific data namespace. undefined for non-D&D sessions. */
  dnd5e?: Dnd5eSheetData;
  /** Optional group name for organizing sheets in the character panel */
  group?: string;
  /** T-13: where the sheet lives (GM organisation, never sent to players). Absent = shared. */
  holder?: MapOwner;
  /** v33: Visibility for non-owners. Default: true (PC), false (NPC) */
  visible: boolean;
  /** T204: Append-only audit log of mutations. Optional for backward-compat with v40 sheets that lack the field; handlers normalize to [] when reading. */
  auditLog?: AuditEntry[];
  /** Diagnostic AI observations (GM+owner only). Bounded by AI_OBSERVATIONS_MAX. Stripped for non-owner non-GM viewers. */
  aiObservations?: AiObservation[];
}

// --- Dice ---

export interface DicePart {
  type: 'dice' | 'mod';
  count?: number;
  sides?: number;
  rolls?: number[];
  kept?: number[];
  sum?: number;
  value?: number;
}

export interface DiceResult {
  formula: string;
  parts: DicePart[];
  total: number;
  details: string;
  timestamp: string;
}

// --- Chat ---

/**
 * Spell-cast chat card payload. Display-only snapshot taken at cast time so the
 * card stays correct even if the sheet later changes. `target` is reserved for a
 * future "cast against a specific token" flow; for now casts go "into the void"
 * (target undefined).
 */
export interface SpellCastInfo {
  name: string;
  /** 0 = cantrip */
  level: number;
  school?: string;
  components?: { v?: boolean; s?: boolean; m?: boolean; materialDesc?: string };
  concentration?: boolean;
  ritual?: boolean;
  castingTime?: string;
  range?: string;
  duration?: string;
  /** Save-based spell: which save + DC, and whether success halves. */
  save?: { ability: string; dc: number; halfOnSuccess?: boolean };
  /** Full spell text — rendered collapsed, expandable on the card. */
  description?: string;
  /** "At higher levels…" scaling text. */
  higherLevels?: string;
  /** Reserved: name of a targeted token (future auto-cast-at-target). */
  target?: string;
}

export interface ChatMessage {
  id: string;
  author: string;
  text: string;
  type: 'text' | 'roll' | 'system' | 'gm' | 'sheet-roll' | 'ai' | 'sticker' | 'spell-cast';
  result?: number;
  details?: string;
  time: string;
  rollResult?: RollResult;
  /** Generated image URL (persistent, after download from Replicate) */
  imageUrl?: string;
  /** Image generation status: generating → ready | error; deleted — the picture was deleted in the gallery */
  imageStatus?: 'generating' | 'ready' | 'error' | 'deleted';
  /** Original prompt used for image generation */
  imagePrompt?: string;
  /** v34: Whisper recipients (participantId[]). undefined = public message */
  whisperTo?: string[];
  /** v34: Speaking as a character (snapshot at send time) */
  speakingAs?: { name: string; color?: string };
  /** v34: Sticker reference */
  sticker?: { packId: string; stickerId: string };
  /** v34: Inline roll results (server-computed) */
  inlineRolls?: InlineRollResult[];
  /** Spell-cast card payload (type === 'spell-cast') */
  spellCast?: SpellCastInfo;
  /** A system line the client words in the viewer's language: i18n key + {param} values; `text` is the fallback. */
  textKey?: string;
  textParams?: Record<string, string>;
}

/** Result of a single inline roll [[formula]] evaluated on the server */
export interface InlineRollResult {
  /** Original formula, e.g. "2d6+3" */
  formula: string;
  /** Computed total */
  total: number;
  /** Roll breakdown, e.g. "2d6[4,3]+3" */
  details: string;
  /** Start index of [[ in the original text */
  startIndex: number;
  /** End index (after ]]) in the original text */
  endIndex: number;
}

/** Sticker pack definition (static data, not persisted in SessionState) */
export interface StickerPack {
  packId: string;
  name: string;
  stickers: StickerDef[];
}

/** Individual sticker definition */
export interface StickerDef {
  stickerId: string;
  name: string;
  filename: string;
}

/** Custom sticker pack metadata (from Prisma, sent to client) */
export interface CustomStickerPack {
  id: string;
  userId: string;
  name: string;
  stickers: CustomStickerItem[];
  createdAt: string;
}

/** Individual custom sticker item */
export interface CustomStickerItem {
  id: string;
  name: string;
  /** URL path for image: /api/sticker-packs/:packId/stickers/:id/image */
  imageUrl: string;
  width: number;
  height: number;
}

// --- Initiative ---

export interface InitiativeMember {
  tokenId: string;
  name: string;
}

/**
 * One line of the initiative order (v46, initiative brief 25.10). A group of identical monsters is ONE entry with
 * `members` (each keeps its token); a single participant has no `members`. The turn order is `order` (higher acts
 * first: the number, then the Dexterity tie-break, then a drag); `value` is what is shown.
 */
export interface InitiativeEntry {
  /** Entry id: the token id of a single participant, `grp-<first token>` for a group. */
  id: string;
  /** The first member's token (kept for every older reader: portraits, HUD, subscribers). */
  tokenId: string;
  name: string;
  /** null = a player character that has not rolled yet. */
  value: number | null;
  color: string;
  /** A group of identical monsters (two or more); absent for a single participant. */
  members?: InitiativeMember[];
  /** Sort key: higher acts first. */
  order: number;
  /** Dexterity modifier — the tie-break (GM only; never sent to players). */
  dex?: number;
  /** No sheet: rolled with +0 (GM only). */
  noSheet?: boolean;
  /** A player character: rolls himself, keeps his turn at 0 hit points. */
  pc?: boolean;
  /** The round in which this entry has acted. */
  acted?: number;
  /** «следующий»: its number moved above the current one before it acted — acts right after the current one. */
  owed?: boolean;
  /** «с раунда N»: added mid-round after its place had passed. */
  fromRound?: number;
  /** Delayed its turn in round N (sits in «Отложили ход» until it joins or the round ends). */
  delayedRound?: number;
  /** Members marked «Выбыл» by hand (a token without hit points). GM only. */
  out?: string[];
}

/** One step of the turn («next», «delay», «join») as it was before — what «Назад» restores. GM only, not persisted. */
export interface InitiativeTurnStep {
  cur: string | null;
  anchor?: number;
  round: number;
  entries: Record<string, { acted?: number; owed?: boolean; delayedRound?: number; fromRound?: number; order: number; value: number | null }>;
}

export interface InitiativeUndo {
  id: string;
  /** What is undone (the toast's words). */
  kind: 'add' | 'remove' | 'end';
}

export interface InitiativeState {
  entries: InitiativeEntry[];
  /** Index (in `entries`) of the one who acts; -1 = nobody (round 0 — waiting for rolls; a hidden turn for a player). */
  currentIndex: number;
  /** 0 = the fight is assembled, round 1 has not started (waiting for the players' rolls). */
  round: number;
  /** GM-managed visibility of the initiative panel. Persisted in session
   * state so late joiners / reloads / page switches restore an open panel
   * instead of losing the one-shot broadcast (owner feedback 2026-07-08). */
  panelVisible?: boolean;
  /** `order` of the current turn's place: where «next» continues when the current entry left. GM only. */
  anchor?: number;
  /** Tokens already seen by this fight (on the map when it started, added, or dismissed from «На карте: …»). GM only. */
  known?: string[];
  /** The last undoable change (add / remove / end). GM only. */
  undo?: InitiativeUndo;
  /** Who was in the last fight («Те же участники»). GM only. */
  lastRoster?: string[];
  /** Rounds of the fight that just ended («Бой закончен · раундов: N»). */
  endedRounds?: number;
  /** The steps of the turn so far («Назад» undoes the last). GM only, kept in memory (a restart falls back to the place). */
  turnHistory?: InitiativeTurnStep[];
  /** The fight's turn clock (which turns have started). Server only: never sent to anyone, persisted in initiatives.extra. */
  turnClock?: TurnClock;
}

/**
 * Which turns of this fight have started (docs/backlog/tia-turn-clock-design-2026-10-28.md). A start is counted only on a
 * forward command (next / delay / remove) that gave the turn to the final current entry; «Назад», «Отменить», a late
 * roll, join, split and moves never start a turn.
 */
export interface TurnClock {
  /** New on initiative:start (or when a fight running before the clock existed first commits). */
  fightId: string;
  /** Latched once the first forward commit leaves round 1's provisional order (somebody acted); never reset. */
  confirmed: boolean;
  /** Commits with counted starts in this fight; only grows. */
  seq: number;
  /** Latched when a token would have to be counted over TURN_CLOCK_CAP: no more starts in this fight. */
  full: boolean;
  /** tokenId → the last round in which its turn was counted. At most TURN_CLOCK_CAP keys; never evicted. */
  seen: Record<string, number>;
}

// --- Fog ---

export type FogReveal =
  | { type: 'circle'; x: number; y: number; radius: number }
  | { type: 'rect'; x: number; y: number; width: number; height: number }
  | { type: 'polygon'; points: Array<{ x: number; y: number }> };

export interface FogState {
  enabled: boolean;
  mode: 'manual' | 'dynamic';
  revealed: FogReveal[];
  closings: FogReveal[];
  explored: FogReveal[];
}

// --- Annotation & Trigger Geometry ---

export type AnnotationArea =
  | { type: 'rect'; x: number; y: number; width: number; height: number }
  | { type: 'circle'; cx: number; cy: number; radius: number }
  | { type: 'polygon'; points: Array<{ x: number; y: number }> };

export interface MapAnnotation {
  id: string;
  area: AnnotationArea;
  label: string;
  terrain?: TerrainType;
  cover?: CoverType;
  description?: string;
  visibility: 'gm' | 'all';
}

export type TriggerCondition =
  | { type: 'token_enters_area'; area: AnnotationArea; tokenFilter?: TokenFilter }
  | { type: 'token_hp_below'; tokenId: string; threshold: number }
  | { type: 'all_tokens_dead'; tokenIds: string[] }
  | { type: 'initiative_round'; round: number }
  | { type: 'manual'; label: string };

export type TriggerAction = { tool: string; args: Record<string, unknown> };

export interface MapTrigger {
  id: string;
  condition: TriggerCondition;
  actions: TriggerAction[];
  oneShot: boolean;
  enabled: boolean;
  label: string;
  createdAt: string;
}

// --- Map Objects ---

export interface MapObjectProperties {
  cover?: ObjectCover;
  light?: { radius: number; color?: string };
  hp?: number;
  maxHp?: number;
  damage?: string;
  locked?: boolean;
  lockDc?: number;
  state?: ObjectState;
  traversable?: boolean;
  difficultTerrain?: boolean;
  custom?: Record<string, string | number | boolean>;
}

export interface MapObjectStyle {
  color?: string;
  icon?: string;
  opacity?: number;
}

export interface MapObject {
  id: string;
  label: string;
  description: string;
  region: LinkRegion;
  category: MapObjectCategory;
  properties: MapObjectProperties;
  visibility: 'gm' | 'all';
  interactable: boolean;
  style: MapObjectStyle;
  sortOrder: number;
}

// --- Session Settings ---

export interface SessionSettings {
  measurementMode: MeasurementMode;
  /** 29.10 owner: whether Tia deletes content without the GM's button (delete_content). Absent/false — every delete asks. */
  aiDeleteWithoutAsking?: boolean;
  /** The table's language — the GM UI's (its presets, month names, labels): written when the GM creates or joins (04.11). */
  locale?: 'ru' | 'en';
  /** Grid size in game-world feet per cell (default: 5). Session-level. Used by ruler/measurement tools. */
  gridSizeFt: number;
  // Player AI settings (v27)
  /** Whether player AI assistant is enabled (default: true) */
  playerAiEnabled: boolean;
  /** Token budget per hour per participant. null = unlimited */
  playerAiTokenBudgetPerHour: number | null;
  /** Rate limit: max requests per minute per participant (default: 10) */
  playerAiRateLimitPerMinute: number;

  // v28: AI @mention settings (Spec 050-A)
  /** Имя AI ассистента для @mention детекции (default: "Tiah") */
  aiAssistantName: string;
  /** Включён ли @mention AI в общем чате (default: true) */
  aiMentionEnabled: boolean;
  /** Rate limit: макс @mention запросов/мин на участника (default: 5) */
  aiMentionRateLimitPerMinute: number;

  // v28: Image generation settings (Spec 050-B)
  /** Модель картинок кампании по умолчанию: имя OpenRouter (`google/gemini-3.1-flash-image`); старое "flux-schnell" = по умолчанию сервера */
  imageGenModel: string;
  /** Текстовая модель ассистента кампании по умолчанию (имя OpenRouter); пусто — по умолчанию сервера. Пакет 3 переезда AI. */
  aiTextModel?: string;
  /** Стилевой prefix для промптов генерации (default: "") */
  imageGenStylePrefix: string;
  /** Rate limit: макс генераций/час на сессию (default: 10) */
  imageGenRateLimitPerHour: number;

  // v31: Display settings
  /** Grid visual style (default: 'lines') */
  gridStyle: GridStyle;
  /** Grid opacity 0-100 (default: 100) */
  gridOpacity: number;
  /** Default token size for new tokens (default: 'medium') */
  defaultTokenSize: TokenSize;
  /** HP bar visibility mode (default: 'hover') */
  hpBarVisibility: HpBarVisibility;

  // v32: Combat rules
  /** Auto-roll initiative from sheet DEX mod (default: false) */
  autoRollInitiative: boolean;
  /** Show confirm dialog before applying damage (default: false) */
  confirmDamage: boolean;

  /** D&D 5e-specific settings namespace. undefined for non-D&D sessions. */
  dnd5e?: Dnd5eSessionSettings;

  // v33: Player permissions
  /** Granular player permissions */
  playerPermissions: PlayerPermissions;

  // v34: Display — ambient overlay
  /** Show ambient time-of-day color overlay on canvas (default: true) */
  ambientOverlayEnabled: boolean;

  // v37: Custom stickers
  /** Allow players to create and connect sticker packs (default: false) */
  allowPlayerStickers: boolean;
}

// --- Auth ---

/** Public user profile (never includes passwordHash) */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
}

/** Response for login/register endpoints */
export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  // refresh token is in httpOnly cookie, not in response body
}

/** @deprecated Use CampaignListItem */
export type SessionListItem = CampaignListItem;

// --- Roles & Participants ---

export interface CampaignMember {
  id: string;
  name: string;
  role: Role;
  online: boolean;
  assignedTokenIds: string[];
  userId?: string;
  /** User-chosen accent color (hex, e.g. "#6366f1"). Used for portrait border and map pings. */
  color?: string;
}

/** @deprecated Use CampaignMember */
export type Participant = CampaignMember;

// --- Session / Campaign ---

export interface CampaignInfo {
  id: string;
  code: string;
  slug: string | null;
  createdAt: string;
  gmName: string;
  participantCount: number;
  ownerId?: string;
}

/** @deprecated Use CampaignInfo */
export type SessionInfo = CampaignInfo;

export interface CampaignListItem {
  id: string;
  code: string;
  slug: string | null;
  name: string;
  role: 'gm' | 'player';
  participantCount: number;
  createdAt: string;
}

// --- Map Notes ---

/** @deprecated Use MapPin instead. Kept for backward compat and migration. */
export interface MapNote {
  id: string;
  pageId: string;
  x: number;
  y: number;
  title: string;
  content: string;
  imageUrl?: string;
  icon: NoteIcon;
  color: string;
  visibility: 'gm' | 'all';
  createdBy: string;
  createdAt: string;
  parentNoteId?: string | null;
}

// --- Map Pins (replaces MapNote) ---

/** Thin map marker — position + visual + reference to JournalEntry. No content storage. */
export interface MapPin {
  id: string;
  pageId: string;
  x: number;
  y: number;
  journalEntryId: string | null;
  icon: NoteIcon;
  color: string;
  visibility: 'gm' | 'all' | 'owner';
  createdBy: string;
  createdAt: string;
}

// --- Beats (ordered narrative units) ---

/** Lifecycle status of a Beat. Mirrors the Prisma BeatStatus enum. */
export type BeatStatus = 'draft' | 'ready' | 'played' | 'carried';

/**
 * A Beat is an ordered narrative unit within a campaign. It references a
 * JournalEntry via a plain scalar `journalEntryId` (mirroring MapPin) so the
 * JournalEntry stays untouched. `sessionLabel` is a free-form label — the
 * schema has no dedicated campaign-session model, so a string label matches
 * the actual data model.
 */
export interface Beat {
  id: string;
  campaignId: string;
  sessionLabel: string | null;
  journalEntryId: string | null;
  /**
   * The primary map: one of `pageIds`, or null (no primary). The default
   * target of «open the beat's map»; never shown to players by itself.
   * Before v45 — the beat's only map. Read it through beatPrimaryMapId.
   */
  pageId: string | null;
  /**
   * v45 (GM Studio 6): the beat's maps — explicit links, in the GM's order.
   * Never derived from tabs, open panes or location snapshots. Invariant:
   * pageId is null or one of these. Only the server's beat commands change
   * them, both in one operation (shared/beat-maps.ts). A deleted map's id
   * stays here until the GM removes the link.
   */
  pageIds: string[];
  order: number;
  status: BeatStatus;
  /**
   * v43 (GM Studio 3.1): the beat's explicit materials — journal entries and
   * sheets the GM added to this beat. Independent of journal links and of other
   * beats sharing the same prep source; the prep source (journalEntryId) and
   * the maps (pageIds / pageId) keep their own fields.
   */
  materials: BeatMaterialRef[];
  /**
   * P04 (Q-4): the forks of this beat — «if <label> → <beat>». Absent = none.
   * GM-only like the beat itself. Removing a fork never deletes the beat it led to.
   */
  outcomes?: BeatOutcome[];
  /**
   * P04 (Q-4): not null — a reserve beat of that beat. It is not in the main
   * line of the plan (not numbered); it hangs right under its beat.
   */
  reserveOf?: string | null;
}

/** One fork of a beat: a label stored as the GM typed it, and the beat it leads to. */
export interface BeatOutcome {
  id: string;
  label: string;
  toBeatId: string;
}

/** A typed reference to a beat material (see Beat.materials). */
export interface BeatMaterialRef {
  kind: 'journal' | 'sheet';
  id: string;
}

// --- AI Drafts (staged tool proposals) ---

/**
 * An AiDraft is a staged, human-reviewable proposal for a single AI tool call.
 * `args` holds the raw JSON tool arguments; `entityRef` points at the affected
 * domain object; `diff` is a human-readable old->new summary. Lifecycle mirrors
 * a review queue: pending -> applied | rejected, with reverted for undo.
 */
export interface AiDraft {
  id: string;
  conversationId: string;
  groupId: string;
  toolName: string;
  /** Raw JSON tool arguments */
  args: unknown;
  entityRef: {
    kind: 'sheet' | 'journal' | 'page' | 'token' | 'other';
    id?: string;
    label: string;
  };
  /** Human-readable old->new summary */
  diff: string;
  risk: 'low' | 'high';
  status: 'pending' | 'applied' | 'rejected' | 'reverted';
  conflict?: boolean;
  createdAt: number;
}

/**
 * Where the GM is when a request to the AI is sent (AT-4): what is open in the main panel of Studio (a quest is a
 * journal record), or the state of the table. The AI is told it with names and ids.
 */
export type AiFocus =
  | { place: 'studio'; kind: 'beat' | 'journal' | 'page' | 'sheet' | 'library' | 'remarks'; id?: string }
  | { place: 'table'; beatId: string | null; myPageId: string | null; playersPageId: string | null; tokenIds: string[] };

// --- AI changes (the card of an answer: «Изменения (N)», undo by the journal) ---

/** What the AI's answer did to one thing of the campaign — one line of the change card, one step of the undo journal. */
export interface AiChange {
  id: string;
  op: 'create' | 'update' | 'delete';
  entity: {
    kind: 'beat' | 'outcome' | 'journal' | 'objective' | 'timeline' | 'page' | 'pin' | 'sheet' | 'token';
    id: string;
    name: string;
    /** The thing it hangs on (an outcome's beat, an objective's quest) — what the inverse command needs. */
    parentId?: string;
  };
  /** update: which field ('description', 'content', 'gmNotes', 'currentBeatId', …). */
  field?: string;
  /** update: was; text as a string (for «было / стало»). */
  before?: unknown;
  /** update: now. */
  after?: unknown;
  /** update: the part of the thing the line is about, by its name (an item of a sheet that was put on). */
  label?: string;
  /** The change this one goes with (a note in a new beat, a fork into it → the change of the beat). */
  dependsOn?: string;
  /** Undone (↺). */
  undone?: boolean;
  /** An undo kept the GM's own later edit here (resolve 'keep-mine'). */
  kept?: boolean;
  /** No inverse exists (a deletion): shown, not undoable. */
  irreversible?: boolean;
}

/** One answer of the AI that changed data: its changes, in the order they were made. Stored per campaign (GM only). */
export interface AiTurnRecord {
  turnId: string;
  conversationId: string;
  /** The member who asked. */
  participantId: string;
  at: number;
  /** Set when the turn is a plan applied by «Do it» (ai:plan-apply). */
  planId?: string;
  changes: Array<AiChange & {
    /** Server-only: what the thing looked like when the answer ended — a later edit differs. */ fp?: string;
    /** Server-only: a deleted thing kept whole, so that undo can make it again. */ restore?: unknown;
    /** Server-only: the tool call whose run the change was observed in (absent in older records). */ tool?: string;
    /** Server-only (v50): the operation the change was observed in — action-ledger operationKey of turn + tool_use id. Absent in older records. */ opKey?: string;
    /** Server-only (v50): why the change has no undo when the journal decided so — 'shared-with-foreign': another member changed the same field inside the same command; 'foreign-unattributed': another member wrote inside the same command and their part could not be captured. */ reason?: string;
  }>;
  /**
   * v50: what the answer may have changed that the journal could not attribute (a tool that writes outside an observed
   * boundary, a write after the answer ended). Shown as «not recorded — no undo»; absent when there is none.
   */
  untracked?: AiUntrackedWrite[];
}

/** A write of an answer the journal could not attribute to a line (AiTurnRecord.untracked). */
export interface AiUntrackedWrite {
  tool: string;
  reason: 'late-write' | 'capture-failed' | 'async-direct-write' | 'other-campaign';
}

/** One step of a plan the AI offered (ai:plan). field / before / after: a text edit as was / now. */
export interface AiPlanStepWire {
  id: string;
  tool: string;
  entity?: { kind: AiChange['entity']['kind']; id: string; name: string };
  dependsOn?: string;
  args?: Record<string, unknown>;
  field?: string;
  before?: unknown;
  after?: unknown;
}

// --- Remarks (GM Studio «Замечания», group E P13) ---

export type RemarkCheck = 'duplicate' | 'empty-prep' | 'empty-npc' | 'broken-link' | 'field-misuse';

/** Evidence of a remark: a thing of the campaign, opened in Studio by a click. */
export interface RemarkLink { kind: 'beat' | 'journal' | 'page' | 'sheet'; id: string; name: string; objectiveId?: string }

/** One remark: simple (no model, computed from the data), prep (the model's opinion) or game (a hint from the play). */
export interface Remark {
  /** simple: check:id[,id] (stable); prep / game: prep:<uuid> / game:<uuid>. */
  key: string;
  kind: 'simple' | 'prep' | 'game';
  check?: RemarkCheck;
  /** simple: empty — the client writes it from check + links (i18n); prep / game: the model's text. */
  title: string;
  body: string;
  links: RemarkLink[];
  /** There is a fix (what exactly — on the server for simple ones; for prep — fixPrompt is a request to Tia). */
  fix?: { label: string; confirm?: string };
  /** prep (model): the request the «fix» button sends to Tia. */
  fixPrompt?: string;
  questId?: string;
  objectiveId?: string;
  beatId?: string;
  stale?: boolean;
}

export interface RemarkSettings { gameHints: boolean; prepHints: boolean; offerFixes: boolean; simpleChecks: boolean }

/** campaign.state.remarks — GM only (cut from every player-bound state), kept in state_extras. */
export interface RemarksState {
  settings: RemarkSettings;
  /** prep + game remarks, the model's. */
  model: Remark[];
  dismissed: Array<{ key: string; fingerprint: string }>;
}

// --- Location Links ---

export type LinkRegion =
  | { type: 'rect'; x: number; y: number; width: number; height: number }
  | { type: 'polygon'; points: Array<{ x: number; y: number }> }
  | { type: 'circle'; cx: number; cy: number; radius: number };

export interface LocationLink {
  id: string;
  targetPageId: string;
  label: string;
  region: LinkRegion;
  style?: 'portal' | 'door' | 'stairs' | 'area';
  visibility?: 'gm' | 'all';
}

// --- Walls (Dynamic Lighting) ---

export interface Wall {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  blocking: WallBlockingType;
  doorState?: DoorState;
  color?: string;
  sourceObjectId?: string;
}

// --- Token Auras ---

export interface TokenAura {
  id: string;
  radiusFt: number;
  color: string;       // hex color
  opacity: number;     // 0-1, default 0.15
  label: string;
  style: 'solid' | 'dashed' | 'pulse';
  visible: 'all' | 'gm';
}

// --- Token (serialized, no runtime DOM refs) ---

export interface SerializedToken {
  id: string;
  name: string;
  x: number;
  y: number;
  size: number;
  color: string;
  imageSrc: string | null;
  hp: number;
  maxHp: number;
  ac: number;
  conditions: string[];
  description: string;
  notes: string;
  visible: boolean;
  characterSheetId?: string;
  auras: TokenAura[];
  /** Настройки видимости для dynamic lighting. Optional — отсутствие = нет vision (декорация) */
  vision?: TokenVision;
  /** Источник света токена (факел, заклинание). Optional — отсутствие = нет света */
  light?: TokenLight;
  /** Тактический профиль поведения NPC (используется AI-инструментами). Optional — backward compat */
  behavior?: NpcBehaviorProfile;
  /**
   * Wire only, never stored: the coarse HP bar (0..1, steps of 0.1) a player gets for a token whose numbers they may
   * not read (the GM's «HP bar» setting always / hover); null — no bar. Set together with hp 0 / maxHp 0.
   */
  hpRatio?: number | null;
}

/** Настройки видимости токена для dynamic lighting */
export interface TokenVision {
  /** Участвует ли токен в LoS расчётах (false для декораций/объектов) */
  enabled: boolean;
  /** Максимальная дальность видимости в ft. 0 = unlimited */
  range: number;
  /** Darkvision дальность в ft. 0 = нет darkvision */
  darkvision: number;
  /** Тип зрения. undefined/'normal' = обычное. Future: 'blindsight', 'tremorsense' */
  visionType?: 'normal' | 'darkvision';
}

/** Источник света, привязанный к токену (факел, заклинание Light, Daylight) */
export interface TokenLight {
  /** Радиус яркого света (ft). 0 = нет яркого света */
  brightRadius: number;
  /** Радиус тусклого света (ft). Обычно = brightRadius × 2. 0 = нет dim */
  dimRadius: number;
  /** Цвет света (hex, e.g. '#FFA500'). undefined = warm white default */
  color?: string;
  /** Включён ли источник. false = свет не излучается */
  enabled: boolean;
}

/** Глобальное освещение страницы (дневной свет, сумерки, полная тьма) */
export interface GlobalIllumination {
  level: 'bright' | 'dim' | 'dark';
  color?: string;
}

// --- Page ---

export interface PageState {
  id: string;
  name: string;
  sortOrder: number;
  mapUrl: string | null;
  /** Grid size in canvas pixels per cell (default: 70). Page-level. Used by rendering engine for grid/token placement. */
  gridSize: number;
  /** Map image scale factor (default: 1.0, range: 0.1-5.0) */
  mapScale?: number;
  /** F-8: shift of the map image against the grid, image pixels (default 0). Set only by page:update-settings. */
  mapOffsetX?: number;
  mapOffsetY?: number;
  /** Canvas background color (CSS hex, e.g. "#1a1f2b"). Defaults to theme --bg-dark. */
  bgColor?: string;
  tokens: SerializedToken[];
  drawings: Stroke[];
  fog: FogState;
  parentPageId: string | null;
  /** Snapshots of one location share this id (null = standalone / group-of-one). */
  locationGroupId?: string | null;
  /** P03: whose the map is. Absent = shared. GM organisation: never sent to players. */
  owner?: MapOwner;
  locationLinks: LocationLink[];
  /** @deprecated Use pins. Kept for legacy compat layer. */
  notes: MapNote[];
  pins: MapPin[];
  annotations: MapAnnotation[];
  triggers: MapTrigger[];
  textObjects: TextObject[];
  mapObjects: MapObject[];
  walls: Wall[];
  /** Глобальный уровень освещения страницы (bright = дневной свет по умолчанию) */
  globalIllumination: GlobalIllumination;
}

export interface PageInfo {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  tokenCount: number;
  hasMap: boolean;
  parentPageId: string | null;
  /** Snapshots of one location share this id (null = standalone / group-of-one). */
  locationGroupId?: string | null;
  /** P03: whose the map is. Absent = shared. Sent to GMs only. */
  owner?: MapOwner;
}

/** P03: whose a map is — the shared list or one beat (hidden from the Library lists). */
export type MapOwner = { kind: 'shared' } | { kind: 'beat'; beatId: string };

/**
 * P03: whose a journal entry is. GM organisation of preparation — never sent to
 * players, independent of `visibility`. Absent = shared.
 * `quest` is the owner of a prep text shared by several beats of one quest (P-1).
 */
export type JournalEntryOwner =
  | { kind: 'shared' }
  | { kind: 'beat'; beatId: string }
  | { kind: 'objective'; questId: string; objectiveId: string }
  | { kind: 'quest'; questId: string };

// --- Journal Entries ---

export interface JournalNpcData {
  portrait?: string;
  race?: string;
  occupation?: string;
  disposition: NpcDisposition;
  description: string;
  personality: string;
  secrets: string;
  questHooks: string[];
  alive: boolean;
}

export interface JournalLocationData {
  image?: string;
  parentEntryId?: string;
  description: string;
  secrets: string;
  pageId?: string;
}

/**
 * One objective of a quest (GM Studio quests 1): a title (`text`) and a
 * description. `id` is stable: the GM's tick (`journal:objective-set`) and the
 * editor address the objective by it, so a tick and an edit of the list made
 * at the same time never lose each other (see the helpers below).
 * A player gets the objective — description included — only when it is not
 * hidden and the quest's objectives are open to them (stripGmFields).
 */
export interface QuestObjective {
  id: string;
  text: string;
  /** Absent on objectives saved before descriptions existed — the same as ''. */
  description?: string;
  completed: boolean;
  hidden: boolean;
}

export interface JournalQuestData {
  status: QuestStatus;
  questGiverEntryId?: string;
  description: string;
  gmNotes: string;
  objectives: QuestObjective[];
  rewards?: string;
  /**
   * Q-5: this quest follows from another one (optionally from one of its objectives). GM only —
   * stripGmFields removes it from every copy a player gets. Setting it never changes a status or a visibility.
   */
  followsFrom?: QuestFollowsFrom | null;
}

export interface QuestFollowsFrom {
  questId: string;
  objectiveId?: string;
}

// --- Quest objectives (GM Studio quests, package 1) ---
/*
 * An objective has a stable `id`. Its tick (`completed`) is changed by one
 * command only — `journal:objective-set` (entry, objective, value) — and the
 * server keeps it. An edit of the list (`journal:update` with `objectives`:
 * order, texts, descriptions, hidden flags, added / removed objectives) is
 * made from the editor's copy, which may be older than a tick another GM just
 * made; so the server takes each known objective's tick from its OWN copy,
 * never from the edit. A tick and an edit made at the same time both stay.
 *
 * Objectives saved before ids existed get them ONCE on load
 * (SessionManager.loadCampaign, persisted); the server also gives an id to an
 * objective that comes without one (an older client, the AI, an import).
 */

/** Longest id accepted from a client (a UUID is 36). */
const MAX_OBJECTIVE_ID_LENGTH = 64;

const isObjectiveId = (value: unknown): value is string =>
  typeof value === 'string' && value !== '' && value.length <= MAX_OBJECTIVE_ID_LENGTH;

/**
 * Objectives in the kept shape, or null when `raw` is not a list. A string
 * becomes an objective with that text. An id that is missing, invalid or
 * already used earlier in the list is replaced by `newId()`.
 */
export function normalizeQuestObjectives(raw: unknown, newId: () => string): QuestObjective[] | null {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<string>();
  return raw.map((item: unknown): QuestObjective => {
    const o: Record<string, unknown> =
      typeof item === 'string' ? { text: item } : typeof item === 'object' && item !== null ? (item as Record<string, unknown>) : {};
    const id = isObjectiveId(o.id) && !seen.has(o.id) ? o.id : newId();
    seen.add(id);
    return {
      id,
      text: typeof o.text === 'string' ? o.text : '',
      ...(typeof o.description === 'string' ? { description: o.description } : {}),
      completed: o.completed === true,
      hidden: o.hidden === true,
    };
  });
}

/** True when every objective has a usable id of its own (nothing to normalize on load). */
export function questObjectivesHaveIds(raw: unknown): boolean {
  if (!Array.isArray(raw)) return false;
  const seen = new Set<string>();
  for (const item of raw as unknown[]) {
    const id = typeof item === 'object' && item !== null ? (item as { id?: unknown }).id : undefined;
    if (!isObjectiveId(id) || seen.has(id)) return false;
    seen.add(id);
  }
  return true;
}

/**
 * The edited list with the tick of every objective `current` has taken from
 * `current` (the confirmed copy). An objective `current` does not have — a new
 * one — keeps the edited tick. Used by the server on `journal:update` and by
 * the client wherever an edited copy is laid over the confirmed one.
 */
export function keepObjectiveTicks<O extends { readonly id?: string; readonly completed: boolean }>(
  current: readonly { readonly id?: string; readonly completed: boolean }[],
  edited: readonly O[],
): O[] {
  const ticks = new Map<string, boolean>();
  for (const o of current) if (typeof o.id === 'string') ticks.set(o.id, o.completed);
  return edited.map((o: O): O => {
    const tick = typeof o.id === 'string' ? ticks.get(o.id) : undefined;
    return tick === undefined || tick === o.completed ? o : { ...o, completed: tick };
  });
}

export interface JournalFactionData {
  description: string;
  attitude: string;
  secrets: string;
}

export interface JournalHandoutData {
  content: string;
  image?: string;
  gmNotes: string;
}

export interface JournalLoreData {
  category: string;
  /** «Видят игроки» — the part of a note the players get when it is shown. */
  content: string;
  /** «Для мастера» — never in a copy for a player (stripGmFields). */
  gmNotes?: string;
}

export type JournalData =
  | JournalNpcData
  | JournalLocationData
  | JournalQuestData
  | JournalFactionData
  | JournalHandoutData
  | JournalLoreData;

export interface JournalEntry {
  id: string;
  /** @deprecated Use campaignId */
  sessionId: string;
  campaignId: string;
  type: JournalEntryType;
  name: string;
  tags: string[];
  pinned: boolean;
  visibility: JournalVisibility;
  data: JournalData;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  characterSheetId?: string;
  /** @deprecated Use pinId */
  noteId?: string;
  /** Optional back-reference to primary map pin */
  pinId?: string;
  /** Fields visible to players when visibility='all'. undefined = all visible (backward compat). */
  revealedFields?: string[];
  /** Migration tracking: original MapNote.id this entry was created from */
  sourceNoteId?: string;
  linksFrom?: JournalLink[];
  linksTo?: JournalLink[];
  /** Optional group name for organizing entries in the journal panel */
  group?: string;
  /** P03: whose the entry is (a beat, a quest objective, a quest). Absent = shared. GM-only. */
  owner?: JournalEntryOwner;
}

export interface JournalLink {
  id: string;
  sourceId: string;
  targetId: string;
  relation: string;
  description?: string;
}

// --- Media Alerts ---

export type AlertTrigger =
  | { type: 'manual' }
  | { type: 'initiative:start' }
  | { type: 'initiative:round' }
  | { type: 'token:death' }
  | { type: 'roll:critical' }
  | { type: 'roll:fumble' }
  | { type: 'page:activate'; source?: 'gm' | 'player' | 'any' }
  | { type: 'custom'; eventName: string };

export interface MediaAlert {
  id: string;
  name: string;
  visualUrl: string | null;
  visualType: 'gif' | 'webm' | 'image' | null;
  soundUrl: string | null;
  durationMs: number;
  layout: AlertLayout;
  volume: number;
  trigger: AlertTrigger;
  triggerEnabled: boolean;
  preset: string | null;
  sortOrder: number;
}

export interface FiredAlertPayload {
  alertId: string;
  name: string;
  visualUrl: string | null;
  visualType: 'gif' | 'webm' | 'image' | null;
  soundUrl: string | null;
  durationMs: number;
  layout: AlertLayout;
  volume: number;
  preset: string | null;
}

export interface AlertQueueItem {
  alert: FiredAlertPayload;
  receivedAt: number;
}

export interface PlayerAlertSettings {
  muted: boolean;
  volume: number;
}

// --- Homebrew Types ---

/** Snapshot homebrew-пакета, подключённого к сессии */
export interface HomebrewSessionPackage {
  packageId: string;
  name: string;
  connectedAt: string;  // ISO timestamp
  entries: HomebrewSessionEntry[];
}

/** Snapshot одной homebrew-сущности внутри сессии */
export interface HomebrewSessionEntry {
  id: string;
  type: HomebrewEntryType;
  slug: string;
  name: string;
  data: Record<string, unknown>;
  sourceText: string;
  confidence: number;
}

/** Diff between current session snapshot and library version */
export interface HomebrewDiff {
  added: Array<{ id: string; type: HomebrewEntryType; name: string }>;
  modified: Array<{ id: string; type: HomebrewEntryType; name: string; fields: string[] }>;
  removed: Array<{ id: string; type: HomebrewEntryType; name: string; usedBySheets: string[] }>;
}

/** Package info for list responses */
export interface HomebrewPackageInfo {
  id: string;
  name: string;
  entryCount: number;
  types: HomebrewEntryType[];
  createdAt: string;
}

// --- Visibility Report (Player AI) ---

/** Visibility report sent by client to server for player AI context */
export interface VisibilityReport {
  visibleTokenIds: string[];      // max 100
  visibleNoteIds: string[];       // max 50
  visibleAnnotationIds: string[]; // max 50
  currentPageId: string;
  timestamp: string;
}

// --- Campaign Timeline Types ---

export interface InWorldTime {
  /** Free text: "3rd day of journey", "After the battle at the bridge" */
  label: string;
  /** Sort key (float). With calendar — auto from date, without — manual */
  sortKey: number;
  /** Structured date fields (optional, only with calendar enabled) */
  year?: number;
  month?: number;
  day?: number;
  timeOfDay?: TimeOfDay;
}

export interface TimelineVisibility {
  mode: 'gm' | 'players' | 'all';
  /** Only when mode='players' — IDs of members who see the event */
  playerIds?: string[];
}

export interface TimelineLink {
  id: string;
  targetType: TimelineLinkTarget;
  targetId: string;
  /** Cached display name for broken link display */
  displayName: string;
}

export interface TimelineEvent {
  id: string;
  title: string;
  /** Markdown description */
  description: string;
  type: TimelineEventType;
  time: InWorldTime;
  visibility: TimelineVisibility;
  tags: string[];
  source: TimelineEventSource;
  status: TimelineEventStatus;
  /** Member ID of creator */
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Linked CampaignSession (optional). null: the session was taken off the event (H-14, «No session»). */
  sessionId?: string | null;
  /** Links to JournalEntry, Sheet, MapPin */
  links: TimelineLink[];
  /** Linked map page IDs */
  pageIds?: string[];
  /** Custom icon (for type 'custom') */
  icon?: string;
  /** Custom accent color (for type 'custom') */
  color?: string;
  /** Order within same sortKey (for drag-n-drop) */
  orderInTime: number;
  /**
   * A draft the server wrote and nobody has touched since: an auto event
   * (combat, death, rest, level, quest objective), a transition written on a
   * map show, or the «Time: …» draft of a world time change. The next show /
   * time change rewrites an untouched transition / time draft. Any GM edit or
   * publishing clears it. Set only by the server (absent on older events = not
   * such a draft).
   */
  autoDraft?: boolean;
}

/**
 * timeline:update-event (GM) never changes `status`, `source` or `createdBy`,
 * whatever the payload says: editing a draft keeps it a draft. Publication goes
 * only through timeline:approve-draft and timeline:auto-transitions; a batch
 * («Publish all auto», «Publish checked and finish») is N approve-draft commands
 * after one confirmation in the UI.
 */
export const TIMELINE_UPDATE_IMMUTABLE_FIELDS = ['status', 'source', 'createdBy'] as const;

/** Tags of a «Переход: <map>» event the server writes when the players change maps. */
export const AUTO_TRANSITION_TAGS = ['auto', 'travel'] as const;

/**
 * An auto transition: written by the server (source is immutable) with both
 * transition tags. Published ones come from a party journey by a location link
 * (and from older builds — every map show); drafts — from a map show.
 */
export function isAutoTransition(event: Pick<TimelineEvent, 'source' | 'tags'>): boolean {
  return event.source === 'system' && AUTO_TRANSITION_TAGS.every((tag) => event.tags.includes(tag));
}

/** The auto transitions still waiting for the GM: drafts, never published by themselves. */
export function unpublishedAutoTransitions<E extends Pick<TimelineEvent, 'source' | 'tags' | 'status'>>(events: readonly E[]): E[] {
  return events.filter((e) => e.status === 'draft' && isAutoTransition(e));
}

/** Tags of the «Time: …» draft the server writes when the GM changes the world time. */
export const AUTO_TIME_TAGS = ['auto', 'time'] as const;

/**
 * The «Time: …» draft: written by the server with both time tags, not yet
 * published. Only an untouched one (`autoDraft === true`) is rewritten by the
 * next time change; a GM edit leaves it alone.
 */
export function isAutoTimeDraft(event: Pick<TimelineEvent, 'source' | 'tags' | 'status'>): boolean {
  return event.status === 'draft' && event.source === 'system' && AUTO_TIME_TAGS.every((tag) => event.tags.includes(tag));
}

/**
 * The one definition of what a member sees in the timeline (panel, fresh join,
 * player AI). A GM sees everything. A player sees a published event visible to
 * all or to selected players that include them, and their own drafts — never
 * anyone else's draft, never a GM-only event.
 */
export function isTimelineEventVisibleTo(
  event: Pick<TimelineEvent, 'status' | 'visibility' | 'createdBy'>,
  memberId: string,
  role: Role,
): boolean {
  if (role === 'gm') return true;
  if (event.status === 'draft') return event.createdBy === memberId;
  if (event.status !== 'published') return false;
  if (event.visibility.mode === 'all') return true;
  if (event.visibility.mode === 'players') return event.visibility.playerIds?.includes(memberId) ?? false;
  return false;
}

export interface CampaignSession {
  id: string;
  /** Session ordinal number */
  number: number;
  title?: string;
  /** Real-world date (ISO string) */
  date?: string;
  /** In-world start time (optional) */
  timeStart?: InWorldTime;
  /** In-world end time (optional) */
  timeEnd?: InWorldTime;
  /** Member IDs of participants */
  participants: string[];
  status: CampaignSessionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarMonth {
  name: string;
  days: number;
  /** Intercalary = inserted period (holiday/special day), not a regular month */
  intercalary?: boolean;
}

export interface CampaignCalendar {
  name: string;
  months: CalendarMonth[];
  /** Days per week (optional) */
  daysPerWeek?: number;
  /** Day-of-week names */
  weekDayNames?: string[];
  /** Year label (e.g. "DR" for Forgotten Realms) */
  yearLabel?: string;
  /** Starting epoch / era description */
  epoch?: string;
}

// --- Lighting ---

// (GlobalIllumination defined above near TokenLight)

// --- Voice Persistent ---

/**
 * Persistent subset of voice session state (Spec 055 055-bugfix).
 * Survives server restart so the 4h STT limit is enforced across reboots
 * and transcript summaries (AI context) are not lost.
 *
 * Runtime-only state (voiceTranscript verbatim window) is NOT persisted
 * here — it lives on Campaign and is regenerated from live audio.
 */
export interface VoiceStatePersistent {
  /** Cumulative STT duration across all bot connections (4h hard limit) */
  totalSttDurationSec: number;
  /** One-shot flag: true once 3.5h warning has been broadcast to GM */
  warningBroadcast: boolean;
  /** Session language for STT (default: 'ru') */
  language: string;
  /** GM-configured Discord → roll1 participant mappings (survives restart) */
  speakerMappings: SpeakerMapping[];
  /** Summarized transcript blocks (>15min old, compressed by the campaign AI model) */
  summaries: TranscriptSummary[];
  /**
   * Full verbatim archive of evicted transcript entries (capped FIFO).
   * Summaries are lossy — the `search_voice_transcript` AI tool digs here
   * when a detail didn't survive compression. Optional: pre-archive
   * campaigns simply have none.
   */
  archive?: VoiceTranscriptEntry[];
}

// --- Push Notifications ---

/** Payload для подписки на push-уведомления */
export interface PushSubscriptionPayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** Payload push-уведомления, отправляемого на устройство */
export interface PushNotificationPayload {
  type: PushEventType;
  title: string;
  body: string;
  sessionCode: string;
  data?: Record<string, string>;
}

// --- AI Campaign Context Types ---

export interface ExcludedEntity {
  id: string;
  name: string;
  aliases?: string[];
}

export type SpoilerGuardEntry =
  | { type: 'entity'; id: string; name: string }
  | { type: 'text'; value: string };

export interface SessionBriefing {
  focus: string;
  activeNpcs: string[];
  spoilerGuard: SpoilerGuardEntry[];
}

// --- AI Persona Types ---

export interface AiPersona {
  type: 'builtin' | 'journal' | 'sheet';
  /** ID: 'tia' для builtin, journalEntryId или sheetId */
  sourceId: string;
  /** Кэшированное отображаемое имя */
  displayName: string;
}

// --- Persona Memory ---

export interface PersonaNote {
  /** UUID заметки */
  id: string;
  /** Текст от первого лица персоны (~100-150 chars) */
  text: string;
  /** Тип: impression (впечатление) | intention (план/обещание GM) */
  tag: 'impression' | 'intention';
  /** ID conversation, из которого пришла заметка */
  conversationId: string;
  /** ISO 8601 timestamp создания */
  createdAt: string;
  /** sourceId персоны (для фильтрации при смене персоны) */
  sourcePersonaId?: string;
  /** v47: откуда заметка — ставит сервер из контекста исполнения, не из ввода tool/текста модели */
  origin?: PersonaNoteOrigin;
  /**
   * v47: кто может её вспомнить. `table` действует только вместе с `origin.channel === 'table'`;
   * отсутствие или любое другое сочетание — `gm` (личная память мастера).
   */
  audience?: PersonaNoteAudience;
}

/** Канал, в котором появилась заметка; `legacy` — заметка до v47 без сведений о канале. */
export interface PersonaNoteOrigin {
  channel: 'private' | 'table' | 'legacy';
  conversationId?: string;
  /** memberId участника, в чьём запросе записана заметка */
  participantId?: string;
}

export type PersonaNoteAudience = 'gm' | 'table';

export interface PersonaMemory {
  /** FIFO, max 10 заметок */
  notes: PersonaNote[];
}

export interface CampaignAiContext {
  worldDescription: string;
  houseRules: string;
  tone: CampaignTone;
  gmInstructions: string;
  excludedEntities: ExcludedEntity[];
  sectionPriority: string[];
  sessionBriefing?: SessionBriefing;
  /** AI persona: undefined = default Tia, null = no persona */
  persona?: AiPersona | null;
  /** Персональная память AI-персоны (056, spec 1) */
  personaMemory?: PersonaMemory;
}

export interface CharacterAiContext {
  backstory: string;
  personality: string;
  playerNotes: string;
}

// --- Campaign Import Types ---

export interface ImportConflict {
  entityId: string;
  entityType: 'token' | 'sheet' | 'page' | 'note';
  name: string;
  existingId: string;
  strategy?: ConflictStrategy;
  newName?: string;
}

export interface ImportPreview {
  pages: number;
  tokens: number;
  sheets: number;
  notes: number;
  estimatedConflicts: number;
  warnings: string[];
}

export interface ImportResult {
  added: number;
  overwritten: number;
  skipped: number;
  errors: string[];
}

// --- Campaign Text Import Types ---

/** Wave 1: catalogization — flat entity list from text */
export interface CatalogEntry {
  slug: string;
  name: string;
  type: CampaignEntityType;
  briefDescription: string;
  confidence: number;  // 0-100
}

/** Wave 3: relations between entities */
export interface CampaignRelation {
  sourceName: string;
  targetName: string;
  relationType: 'location_link' | 'journal_ref' | 'note_ref' | 'npc_location';
}

/** UI state of one pipeline phase */
export interface CampaignImportWave {
  id: 'catalog' | 'detail' | 'relations';
  status: 'pending' | 'running' | 'review' | 'done' | 'error';
  entityCount?: number;
  progress?: { current: number; total: number };
  error?: string;
}

/** Wave 2: detailed entity extraction (discriminated union) */
export type DetailedEntity =
  | CharacterDetail
  | LocationDetail
  | NoteDetail
  | JournalDetail
  | EncounterDetail;

export interface CharacterDetail {
  entityType: 'npc';
  slug: string;
  name: string;
  type: string;
  confidence: number;
  race: string;
  background: string;
  classes: Array<{ class: string; subclass?: string; level: number }>;
  abilities: { str: number; dex: number; con: number; int: number; wis: number; cha: number };
  hp: { max: number; current: number };
  ac: number;
  speed: number;
  spells?: Array<{ id: string; prepared: boolean }>;
  equipment?: Array<{ name: string; quantity: number; equipped: boolean }>;
  features?: Array<{ name: string; description?: string }>;
  proficiencies?: { skills: string[]; tools: string[]; savingThrows: string[] };
  description?: string;
}

export interface LocationDetail {
  entityType: 'location';
  slug: string;
  name: string;
  type: string;
  confidence: number;
  locationType: 'town' | 'dungeon' | 'wilderness' | 'building';
  description: string;
  relatedNpcNames: string[];
  suggestedNotes: Array<{ title: string; content: string }>;
}

export interface NoteDetail {
  entityType: 'note';
  slug: string;
  name: string;
  type: string;
  confidence: number;
  title: string;
  content: string;
  category: string;
  relatedEntityNames: string[];
}

export interface JournalDetail {
  entityType: 'journal';
  slug: string;
  name: string;
  type: string;
  confidence: number;
  title: string;
  content: string;
  category: string;
  relatedEntityNames: string[];
}

export interface EncounterDetail {
  entityType: 'encounter';
  slug: string;
  name: string;
  type: string;
  confidence: number;
  description: string;
  monsters: Array<{ name: string; count: number }>;
  difficulty: 'easy' | 'medium' | 'hard' | 'deadly';
  environment: string;
}

/** Result of one apply wave */
export interface WaveApplyResult {
  wave: number;
  label: string;
  applied: number;
  failed: number;
  errors: string[];
}

// --- Voice Context Types ---

/** Единичная запись транскрипта — результат STT для одного аудиочанка */
export interface VoiceTranscriptEntry {
  /** Уникальный ID записи */
  id: string;
  /** Текст транскрипции */
  text: string;
  /** Discord User ID говорящего */
  discordUserId: string;
  /** Discord display name */
  discordDisplayName: string;
  /** roll1 participant ID (если маппинг настроен) */
  participantId?: string;
  /** Имя персонажа (если маппинг настроен и участник привязан к sheet) */
  characterName?: string;
  /** Метка времени начала речи (ISO 8601) */
  timestamp: string;
  /** Длительность аудиофрагмента в секундах */
  durationSec: number;
  /** Язык транскрипции */
  language: string;
}

/** Суммаризированный блок транскрипта (для фрагментов старше 15 мин) */
export interface TranscriptSummary {
  /** Уникальный ID summary */
  id: string;
  /** Текст суммаризации */
  text: string;
  /** Начало периода (ISO 8601) */
  periodStart: string;
  /** Конец периода (ISO 8601) */
  periodEnd: string;
  /** Количество исходных записей, суммаризированных в этот блок */
  entryCount: number;
  /** Опциональная подсказка о доминирующем говорящем для рендеринга метки в AI-контексте (T001 voice context). Не заполняется текущим пайплайном суммаризации — поле зарезервировано для будущих источников, читается опционально через defensive access. */
  speakerHint?: string;
}

/** Связь Discord-пользователя с участником сессии roll1 */
export interface SpeakerMapping {
  discordUserId: string;
  discordDisplayName: string;
  /** roll1 participant ID */
  participantId: string;
  /** Имя участника в roll1 */
  participantName: string;
  /** ID привязанного character sheet (если есть) */
  characterSheetId?: string;
  /** Имя персонажа (кэшировано для удобства) */
  characterName?: string;
}

/** Информация о голосовой сессии (runtime-only на Campaign) */
export interface VoiceSessionInfo {
  /** Статус подключения */
  status: VoiceConnectionStatus;
  /** Discord Guild ID */
  guildId: string;
  /** Discord Channel ID */
  channelId: string;
  /** Маппинг Discord → roll1 */
  speakerMappings: SpeakerMapping[];
  /** Время подключения (ISO 8601) */
  connectedAt?: string;
  /** Общая длительность STT в секундах (для лимита 4ч) */
  totalSttDurationSec: number;
  /** Язык транскрипции (настройка сессии) */
  language: string;
  /** One-shot flag: true если уведомление о приближении к 4ч лимиту уже отправлено */
  warningBroadcast?: boolean;
  /** Опциональное текстовое сообщение для UI (warning/info баннер) */
  warningMessage?: string;
}

/** P05 (T-4): the four ways out of the current beat (see beat:set-current). */
export type BeatSetCurrentVia = 'outcome' | 'next' | 'back' | 'pick' | 'end';
